import { Card, Typography, Button, Modal } from "antd";
import { Line } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import dayjs from "dayjs";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend } from "chart.js";
import { useRef, useState } from "react";
import { ExpandOutlined, DownloadOutlined } from "@ant-design/icons";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  ChartDataLabels
);

const { Title } = Typography;

export function LlamadasChart({ data = [] }) {
  const chartRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sortedData = [...data].sort((a, b) => 
    new Date(a.fecha) - new Date(b.fecha)
  );
  if (!sortedData || sortedData.length === 0) {
    return (
      <Card style={{ borderRadius: 12 }}>
        <Title level={5}>Llamadas por día</Title>
        <div style={{ height: 420, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography.Text type="secondary">No hay datos para mostrar</Typography.Text>
        </div>
      </Card>
    );
  }

  const labels = sortedData.map((d) =>
    dayjs(d.fecha).format("DD MMM")
  );
  
  const totales = sortedData.map((d) => d.total ?? 0);
  const totalLlamadas = totales.reduce((acc, curr) => acc + curr, 0);
  const promedioLlamadas = totalLlamadas / totales.length;
  const maxLlamadas = Math.max(...totales);
  const minLlamadas = Math.min(...totales);
  const promedioRedondeado = Number(promedioLlamadas.toFixed(2));

  const chartData = {
    labels,
    datasets: [
      {
        label: "Llamadas realizadas",
        data: totales,
        borderColor: "#2979FF",
        backgroundColor: "rgba(41, 121, 255, 0.1)",
        fill: true,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: "#2979FF",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        borderWidth: 3,
        cubicInterpolationMode: 'monotone'
      },
      {
        label: "Promedio",
        data: Array(labels.length).fill(promedioRedondeado),
        borderColor: "rgba(255, 152, 0, 0.6)",
        backgroundColor: "transparent",
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
      }
    ],
  };

  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: { 
        position: "bottom",
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12
          }
        }
      },
      datalabels: {
        display: function(context) {
          const value = context.dataset.data[context.dataIndex];
          return value > 0 && (value === maxLlamadas || value === minLlamadas || context.dataIndex % 2 === 0);
        },
        align: "top",
        anchor: "end",
        color: "#2979FF",
        font: {
          weight: "bold",
          size: 10
        },
        formatter: (value) => (value === 0 ? "" : value),
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderRadius: 4,
        padding: {
          top: 2,
          right: 4,
          bottom: 2,
          left: 4
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#2979FF',
        borderWidth: 1,
        cornerRadius: 6,
        padding: 12,
        displayColors: false,
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            
            if (context.datasetIndex === 1) {
              return `${label}: ${value.toFixed(1)}`;
            }
            return `${label}: ${value}`;
          },
          title: function(tooltipItems) {
            const fecha = sortedData[tooltipItems[0].dataIndex]?.fecha;
            return fecha ? dayjs(fecha).format("DD MMM YYYY") : '';
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11
          },
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          precision: 0,
          font: {
            size: 11
          },
          callback: function(value) {
            return Number.isInteger(value) ? value : '';
          }
        },
        title: {
          display: true,
          text: 'Número de llamadas',
          font: {
            size: 12,
            weight: 'bold'
          }
        }
      },
    },
    elements: {
      point: {
        hoverBackgroundColor: '#fff',
        hoverBorderColor: '#2979FF',
        hoverBorderWidth: 3,
      },
      line: {
        tension: 0.3
      }
    }
  };

  const modalOptions = {
    ...baseOptions,
    plugins: {
      ...baseOptions.plugins,
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 14
          }
        }
      },
      datalabels: {
        ...baseOptions.plugins.datalabels,
        font: {
          weight: "bold",
          size: 12
        }
      },
      tooltip: {
        ...baseOptions.plugins.tooltip,
        padding: 16,
        titleFont: {
          size: 14
        },
        bodyFont: {
          size: 14
        }
      }
    },
    scales: {
      x: {
        ...baseOptions.scales.x,
        ticks: {
          ...baseOptions.scales.x.ticks,
          font: {
            size: 12
          }
        }
      },
      y: {
        ...baseOptions.scales.y,
        ticks: {
          ...baseOptions.scales.y.ticks,
          font: {
            size: 12
          }
        },
        title: {
          ...baseOptions.scales.y.title,
          font: {
            size: 14
          }
        }
      },
    },
  };

  const downloadChart = () => {
    if (chartRef.current) {
      const chartInstance = chartRef.current;
      const originalCanvas = chartInstance.canvas;
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      tempCanvas.width = originalCanvas.width;
      tempCanvas.height = originalCanvas.height;
      tempCtx.fillStyle = 'white';
      tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
      tempCtx.drawImage(originalCanvas, 0, 0);
      const image = tempCanvas.toDataURL('image/png', 1.0);
      const link = document.createElement('a');
      link.download = `llamadas-por-dia-${dayjs().format('YYYY-MM-DD')}.png`;
      link.href = image;
      link.click();
    }
  };

  return (
    <>
      <Card 
        style={{ borderRadius: 12 }}
        extra={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button 
              icon={<ExpandOutlined />} 
              size="small"
              onClick={() => setIsModalOpen(true)}
              title="Maximizar gráfica"
            />
            <Button 
              icon={<DownloadOutlined />} 
              size="small"
              onClick={downloadChart}
              title="Descargar como imagen"
            />
          </div>
        }
      >
        <Title level={5}>
          Llamadas por día
          {totalLlamadas > 0 && (
            <span style={{ fontSize: '14px', color: '#666', marginLeft: 8 }}>
              ({totalLlamadas} total - {sortedData.length} días)
            </span>
          )}
        </Title>
        <div style={{ height: 300 }}>
          <Line 
            ref={chartRef}
            data={chartData} 
            options={baseOptions}
            plugins={[ChartDataLabels]}
          />
        </div>
        {totalLlamadas > 0 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginTop: 16,
            paddingTop: 16,
            borderTop: '1px solid #f0f0f0',
            fontSize: '12px',
            color: '#666'
          }}>
            <div>
              <strong>Promedio:</strong> {promedioLlamadas.toFixed(1)} llamadas/día
            </div>
            <div>
              <strong>Máximo:</strong> {maxLlamadas} llamadas
            </div>
            <div>
              <strong>Mínimo:</strong> {minLlamadas} llamadas
            </div>
          </div>
        )}
      </Card>
      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              Llamadas por día - Vista ampliada
              {totalLlamadas > 0 && (
                <span style={{ fontSize: '14px', color: '#666', marginLeft: 8 }}>
                  ({totalLlamadas} total - {sortedData.length} días)
                </span>
              )}
            </span>
            <Button 
              icon={<DownloadOutlined />} 
              size="small"
              onClick={downloadChart}
            >
              Descargar
            </Button>
          </div>
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width="90vw"
        style={{ maxWidth: 1200 }}
      >
        <div style={{ 
          height: '70vh', 
          minHeight: 500,
          background: 'white',
          padding: 24 
        }}>
          <Line 
            ref={chartRef}
            data={chartData} 
            options={modalOptions}
            plugins={[ChartDataLabels]}
          />
          
          {totalLlamadas > 0 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginTop: 24,
              paddingTop: 24,
              borderTop: '1px solid #f0f0f0',
              fontSize: '14px',
              color: '#666'
            }}>
              <div>
                <strong>Total de llamadas:</strong> {totalLlamadas}
              </div>
              <div>
                <strong>Promedio diario:</strong> {promedioLlamadas.toFixed(1)} llamadas
              </div>
              <div>
                <strong>Día más alto:</strong> {maxLlamadas} llamadas
              </div>
              <div>
                <strong>Día más bajo:</strong> {minLlamadas} llamadas
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}